import React from 'react';
import { useNavigate } from 'react-router-dom';

const About: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-brand">
            <div className="brand-logo">NS</div>
            <span className="brand-text">NexSync</span>
          </div>
          <div className="navbar-actions">
            <button 
              onClick={() => navigate('/')}
              className="btn btn-ghost"
            >
              Home
            </button>
            <button 
              onClick={() => navigate('/signin')}
              className="btn btn-ghost"
            >
              Sign in
            </button>
            <button 
              onClick={() => navigate('/signup')}
              className="btn btn-primary"
            >
              Sign up
            </button>
          </div>
        </div>
      </nav>

      {/* About Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="mb-12 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">About NexSync</h1>
          <p className="text-lg text-gray-600">
            Connecting students with academic professionals since 2025
          </p>
        </div>

        <div className="space-y-12">
          {/* Our Story */}
          <section className="bg-white rounded-xl shadow-md p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-pink-500 rounded-xl flex items-center justify-center">
                <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Our Story</h2>
            </div>
            <div className="space-y-4 text-gray-700">
              <p>
                NexSync was born out of a simple observation: students across Nigeria face 
                immense challenges in completing their academic projects, SIWES reports, 
                and research documentation. At the same time, talented professionals 
                with expertise in various fields were looking for meaningful work.
              </p>
              <p>
                Founded in 2025 by a team of recent graduates and academic professionals, 
                NexSync bridges this gap by creating a secure, transparent platform where 
                students can connect with verified freelancers who specialize in academic 
                writing and project development.
              </p>
            </div>
          </section>

          {/* Mission & Vision */}
          <section className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-8 border border-blue-100 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-700">
                To revolutionize academic project completion by providing students with 
                access to professional support while creating sustainable income opportunities 
                for skilled freelancers across Nigeria.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-8 border border-purple-100 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="-3 0 70 24">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-700">
                To become Nigeria's leading platform for academic project collaboration, 
                empowering millions of students to achieve academic excellence while 
                building a thriving community of professional freelancers.
              </p>
            </div>
          </section>

          {/* Our Values */}
          <section className="bg-white rounded-xl shadow-md p-8">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Our Core Values</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="space-y-4 p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-md">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">Excellence</h4>
                </div>
                <p className="text-gray-700">
                  We maintain the highest standards for all projects and ensure freelancers are 
                  thoroughly vetted and skilled in their respective fields.
                </p>
              </div>

              <div className="space-y-4 p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-md">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">Security</h4>
                </div>
                <p className="text-gray-700">
                  Your data and payments are protected with enterprise-grade security 
                  measures and transparent transaction processes.
                </p>
              </div>

              <div className="space-y-4 p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-md">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">Collaboration</h4>
                </div>
                <p className="text-gray-700">
                  We foster positive collaboration between students and freelancers, 
                  ensuring clear communication and mutual respect throughout every project.
                </p>
              </div>
            </div>
          </section>

          {/* Team Section */}
          <section className="bg-white rounded-xl shadow-md p-8">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Meet Our Leadership</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center group hover:transform hover:scale-105 transition-transform">
                <div className="relative w-28 h-28 bg-gradient-to-br from-blue-400 to-cyan-300 rounded-full mx-auto mb-6 flex items-center justify-center shadow-xl group-hover:shadow-2xl transition-shadow">
                  <span className="text-4xl font-bold text-white">AA</span>
                  <div className="absolute inset-0 rounded-full border-4 border-white/20"></div>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Aminu Abdullahi</h4>
                <p className="text-blue-600 font-medium mb-2">Co-Founder & CEO</p>
                <p className="text-sm text-gray-600 px-4">
                  Former academic researcher with 8+ years in educational technology
                </p>
              </div>

              <div className="text-center group hover:transform hover:scale-105 transition-transform">
                <div className="relative w-28 h-28 bg-gradient-to-br from-emerald-400 to-green-300 rounded-full mx-auto mb-6 flex items-center justify-center shadow-xl group-hover:shadow-2xl transition-shadow">
                  <span className="text-4xl font-bold text-white">FC</span>
                  <div className="absolute inset-0 rounded-full border-4 border-white/20"></div>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Fatima Chukwu</h4>
                <p className="text-emerald-600 font-medium mb-2">Co-Founder & CTO</p>
                <p className="text-sm text-gray-600 px-4">
                  Software engineer specializing in platform development and security
                </p>
              </div>

              <div className="text-center group hover:transform hover:scale-105 transition-transform">
                <div className="relative w-28 h-28 bg-gradient-to-br from-purple-400 to-pink-300 rounded-full mx-auto mb-6 flex items-center justify-center shadow-xl group-hover:shadow-2xl transition-shadow">
                  <span className="text-4xl font-bold text-white">KO</span>
                  <div className="absolute inset-0 rounded-full border-4 border-white/20"></div>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Kemi Okafor</h4>
                <p className="text-purple-600 font-medium mb-2">Head of Operations</p>
                <p className="text-sm text-gray-600 px-4">
                  Project management expert with background in academic administration
                </p>
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl p-10 text-white shadow-2xl">
            <div className="flex items-center gap-4 mb-8 justify-center">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12 1.586l-4 4v12.828l4-4V1.586zM3.707 3.293A1 1 0 002 4v10a1 1 0 00.293.707L6 18.414V5.586L3.707 3.293zM17.707 5.293L14 1.586v12.828l2.293 2.293A1 1 0 0018 16V6a1 1 0 00-.293-.707z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-white">NexSync in Numbers</h2>
            </div>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center group">
                <div className="text-5xl font-bold mb-3 bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">1,500+</div>
                <div className="text-blue-100 font-medium text-lg">Projects Completed</div>
                <div className="w-16 h-1 bg-gradient-to-r from-cyan-300 to-blue-300 mx-auto mt-4 rounded-full"></div>
              </div>
              <div className="text-center group">
                <div className="text-5xl font-bold mb-3 bg-gradient-to-r from-white to-purple-100 bg-clip-text text-transparent">500+</div>
                <div className="text-purple-100 font-medium text-lg">Active Freelancers</div>
                <div className="w-16 h-1 bg-gradient-to-r from-purple-300 to-pink-300 mx-auto mt-4 rounded-full"></div>
              </div>
              <div className="text-center group">
                <div className="text-5xl font-bold mb-3 bg-gradient-to-r from-white to-pink-100 bg-clip-text text-transparent">97%</div>
                <div className="text-pink-100 font-medium text-lg">Success Rate</div>
                <div className="w-16 h-1 bg-gradient-to-r from-pink-300 to-rose-300 mx-auto mt-4 rounded-full"></div>
              </div>
              <div className="text-center group">
                <div className="text-5xl font-bold mb-3 bg-gradient-to-r from-white to-cyan-100 bg-clip-text text-transparent">30+</div>
                <div className="text-cyan-100 font-medium text-lg">Nigerian Universities</div>
                <div className="w-16 h-1 bg-gradient-to-r from-blue-300 to-cyan-300 mx-auto mt-4 rounded-full"></div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Join Our Community</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Whether you're a student needing help with your project or a professional 
                looking to share your expertise, NexSync is the platform for you.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/signup')}
                className="btn btn-primary btn-lg px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
              >
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="-115 0 250 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Start Your Journey
              </button>
              <button 
                onClick={() => navigate('/contactus')}
                className="btn btn-secondary btn-lg px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-800 hover:to-black"
              >
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="-115 0 250 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Contact Us
              </button>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer bg-gradient-to-r from-gray-900 to-black text-white mt-12">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="navbar-brand">
                <div className="brand-logo bg-gradient-to-r from-blue-500 to-cyan-500">NS</div>
                <span className="brand-text text-white">NexSync</span>
              </div>
              <p className="footer-description text-gray-300">
                Empowering students and professionals through academic collaboration.
              </p>
              <div className="flex gap-4 mt-4">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center cursor-pointer hover:scale-110 transition-transform">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                  </svg>
                </div>
                <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg flex items-center justify-center cursor-pointer hover:scale-110 transition-transform">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <div className="w-10 h-10 bg-gradient-to-r from-blue-700 to-blue-900 rounded-lg flex items-center justify-center cursor-pointer hover:scale-110 transition-transform">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z"/>
                  </svg>
                </div>
              </div>
            </div>
            
            <div className="footer-links">
              <div className="footer-column">
                <h4 className="text-white mb-4">Company</h4>
                <button onClick={() => navigate('/about')} className="footer-link text-gray-300 hover:text-white">About</button>
                <button onClick={() => navigate('/contactus')} className="footer-link text-gray-300 hover:text-white">Contact</button>
                <button onClick={() => navigate('/careers')} className="footer-link text-gray-300 hover:text-white">Careers</button>
              </div>
              
              <div className="footer-column">
                <h4 className="text-white mb-4">Resources</h4>
                <button onClick={() => navigate('/helpcenter')} className="footer-link text-gray-300 hover:text-white">Help Center</button>
                <button onClick={() => navigate('/documentation')} className="footer-link text-gray-300 hover:text-white">Documentation</button>
                <button onClick={() => navigate('/blog')} className="footer-link text-gray-300 hover:text-white">Blog</button>
              </div>
              
              <div className="footer-column">
                <h4 className="text-white mb-4">Legal</h4>
                <button onClick={() => navigate('/privacypolicy')} className="footer-link text-gray-300 hover:text-white">Privacy Policy</button>
                <button onClick={() => navigate('/termsofservice')} className="footer-link text-gray-300 hover:text-white">Terms of Service</button>
                <button onClick={() => navigate('/cookiepolicy')} className="footer-link text-gray-300 hover:text-white">Cookie Policy</button>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom border-t border-gray-800 pt-6">
            <p className="text-gray-400">&copy; {new Date().getFullYear()} NexSync. All rights reserved.</p>
            <p className="text-sm text-gray-500 mt-2">Empowering education through technology</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;